// src/components/profile/EditProfileModal.js
import React, { useState } from 'react';
import api from '../../utils/api';
import '../../styles/edit-profile-modal.css';

const EditProfileModal = ({ profile, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    displayName: profile.displayName || '',
    bio: profile.bio || '',
    location: profile.location || '',
    website: profile.website || ''
  });
  
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState(profile.profilePic || '');
  
  const [coverPic, setCoverPic] = useState(null);
  const [coverPicPreview, setCoverPicPreview] = useState(profile.coverPic || '');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Profile picture must be less than 5MB');
        return;
      }
      
      setProfilePic(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleCoverPicChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Cover picture must be less than 5MB');
        return;
      }
      
      setCoverPic(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPicPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Update profile data
      const response = await api.put('/api/users/profile', formData);
      
      let updatedProfile = response.data.profile;
      
      // Upload profile picture if changed
      if (profilePic) {
        const formData = new FormData();
        formData.append('profilePic', profilePic);
        
        const picResponse = await api.post('/api/users/profile/picture', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        updatedProfile.profilePic = picResponse.data.profilePic;
      }
      
      // Upload cover picture if changed
      if (coverPic) {
        const formData = new FormData();
        formData.append('coverPic', coverPic);
        
        const coverResponse = await api.post('/api/users/profile/cover', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        updatedProfile.coverPic = coverResponse.data.coverPic;
      }
      
      onSave(updatedProfile);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="modal-overlay">
      <div className="edit-profile-modal">
        <div className="modal-header">
          <h2>Edit Profile</h2>
          <button className="close-button" onClick={onCancel}>×</button>
        </div>
        
        {error && <div className="modal-error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="image-uploads">
            <div className="cover-upload">
              <label>Cover Photo</label>
              <div 
                className="cover-preview" 
                style={{ backgroundImage: coverPicPreview ? `url(${coverPicPreview})` : 'none' }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverPicChange}
                  disabled={loading}
                />
                <div className="upload-overlay">
                  <i className="upload-icon">📷</i>
                </div>
              </div>
            </div>
            
            <div className="profile-pic-upload">
              <label>Profile Picture</label>
              <div className="profile-pic-preview">
                <img src={profilePicPreview || '/default-avatar.png'} alt="Profile" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePicChange}
                  disabled={loading}
                />
                <div className="upload-overlay">
                  <i className="upload-icon">📷</i>
                </div>
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="displayName">Name</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              maxLength="50"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="bio">Bio</label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              maxLength="500"
              disabled={loading}
            />
            <span className="char-count">{formData.bio.length}/500</span>
          </div>
          
          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              maxLength="100"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              maxLength="100"
              disabled={loading}
            />
          </div>
          
          <div className="modal-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;